import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {
  type InventoryAndContentState,
  InventoryContentOption,
  type TargetableInventoryList,
  TargetableInventoryOption,
} from '@openx/types/targeting/inventoryAndContent';
import { noop } from '@openx/utils/noop';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle';
import { setInventories } from '../../shared/InventoryAndContent/inventoryHelpers';
import { getInventoryDisplayName } from '../../shared/InventoryAndContent/utils';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems';
import { Switch } from '../../shared/Switch/Switch';
import type { TargetingItemsProps } from '../../types';
import { ExcludeInventorySubsetItems } from './ExcludeInventorySubsetItems';

interface InventoryItemsProps extends TargetingItemsProps<InventoryAndContentState> {
  isInventorySelected?: boolean;
}

export const InventoryItems = ({
  readonly = true,
  dispatchInventory = noop,
  targetingParams,
  instanceState,
  isInventorySelected,
  isNotLastItem,
  switchState: chipLineSwitchState,
  showDimensionChipLine = true,
  disabledReasonMessage,
}: InventoryItemsProps) => {
  const { t } = useTranslation();

  const includes = targetingParams[InventoryContentOption.INVENTORY].includes;
  const excludes = targetingParams[InventoryContentOption.INVENTORY].excludes;
  const inventoriesCount = [...includes, ...excludes].length;
  const switchState = !includes.length && !!excludes.length;

  const handleChange = useCallback(
    (targetableInventoryOption: TargetableInventoryOption, filteredItems: TargetableInventoryList) => {
      if (targetableInventoryOption === TargetableInventoryOption.INCLUDES) {
        const filteredItemsNamesSet = new Set(filteredItems.map(item => item.name));

        const filteredExcludes = excludes.filter(item => filteredItemsNamesSet.has(`${item.parent_name}`));

        dispatchInventory({
          payload: filteredExcludes,
          type: setInventories[TargetableInventoryOption.EXCLUDES],
        } as unknown as AnyAction);
      }

      dispatchInventory({
        payload: filteredItems,
        type: setInventories[targetableInventoryOption],
      });
    },
    [dispatchInventory, excludes]
  );

  const handleSwitchChange = useCallback(() => {
    const includesPayload = switchState ? excludes : [];
    const excludesPayload = switchState ? [] : includes;

    dispatchInventory({
      payload: includesPayload,
      type: setInventories[TargetableInventoryOption.INCLUDES],
    } as unknown as AnyAction);
    dispatchInventory({
      payload: excludesPayload,
      type: setInventories[TargetableInventoryOption.EXCLUDES],
    } as unknown as AnyAction);
  }, [dispatchInventory, excludes, includes, switchState]);

  const handleOnClear = useCallback(() => {
    dispatchInventory({
      payload: [],
      type: setInventories[TargetableInventoryOption.INCLUDES],
    } as unknown as AnyAction);
    dispatchInventory({
      payload: [],
      type: setInventories[TargetableInventoryOption.EXCLUDES],
    } as unknown as AnyAction);
  }, [dispatchInventory]);

  const onInventoryRemove = useCallback(
    (itemNames: TargetableInventoryList) => {
      handleChange(switchState ? TargetableInventoryOption.EXCLUDES : TargetableInventoryOption.INCLUDES, itemNames);
    },
    [handleChange, switchState]
  );

  return (
    <Grid>
      {inventoriesCount ? (
        <Box data-test="sub-section">
          <FiltersTitle
            onClear={() => handleOnClear()}
            title={t('Ad Unit [ {count} ]', {
              count: inventoriesCount,
            })}
            readonly={readonly}
            disabledReasonMessage={disabledReasonMessage}
          />
          {!readonly && (
            <Switch
              switchState={switchState}
              onSwitchChange={() => handleSwitchChange()}
              switchOffLabel={t('include')}
              switchOnLabel={t('exclude')}
              groupTitle={t('the following:')}
            />
          )}
          <Grid data-test="section-items">
            <SelectedItems
              filtersTitle={switchState ? t('Excluded') : t('Included')}
              isAddIcon={!switchState}
              selectedItems={switchState ? excludes : includes}
              readonly={readonly}
              handleChange={onInventoryRemove}
              getItemLabel={getInventoryDisplayName}
              isRemovingAll={true}
              dataTest={'inventory'}
            />
            <ExcludeInventorySubsetItems
              readonly={readonly}
              dispatchInventory={dispatchInventory}
              targetingParams={targetingParams}
              instanceState={instanceState}
              isInventorySelected={isInventorySelected}
            />

            {isNotLastItem && showDimensionChipLine && <DimensionChipLine switchState={chipLineSwitchState} />}
          </Grid>
        </Box>
      ) : null}
    </Grid>
  );
};
